<template>
    <div class="bump-times">
        <Card size="small">
            <header>
                <div class="name">{{ $t('venue_statistics.bump_time.headline') }}</div>
                <Select v-if="!historicalData" v-model="select" :options="options"></Select>
            </header>
            <div v-if="!chartData" class="empty-state">
                <p>
                    {{ $t('venue_statistics.age_range.empty_state') }}
                </p>
            </div>
            <div v-if="chartData" class="chart-content">
                <div v-for="(el, i) in chartData" :key="i + 1" class="bar">
                    <div
                        v-if="!historicalData"
                        class="data"
                        :style="{ height: `${chartDataPercentage[i]}%` }"
                        :class="{ zero: el === 0, current: i === chartData.length - 1 }"
                    >
                        <DTooltip> {{ el }} {{ $t('general.visitors') }}</DTooltip>
                    </div>
                    <div
                        v-else-if="historicalData"
                        class="data"
                        :style="{ height: `${chartDataPercentage[i]}%` }"
                        :class="{ zero: el === 0 }"
                    >
                        <DTooltip> {{ el }} {{ $t('general.visitors') }}</DTooltip>
                    </div>
                </div>
                <div v-for="i in 10" :key="i + 100" class="indicator">
                    <div v-if="[1, 4, 7, 10].includes(i)" class="inner-line"></div>
                </div>
                <div v-for="(label, index) in labels" :key="index + 1000" class="label">{{ label }}</div>
            </div>
        </Card>
    </div>
</template>

<script>
import Card from '@/components/Card'
import Select from '@/components/Select'
import DTooltip from '@/components/statistics/DTooltip'
import gql from 'graphql-tag'

export default {
    name: 'BumpTimes',
    components: {
        Card,
        Select,
        DTooltip,
    },
    props: {
        venueAnalytics: {
            type: Object,
            default: undefined,
        },
        historicalData: {
            type: Boolean,
            default: false,
        },
    },
    apollo: {
        venue: {
            query: gql`
                query ($id: ID!) {
                    venue(id: $id) {
                        id
                        email
                        phone
                        websiteUrl
                        dresscode
                        name
                        logoUrl
                        genres
                        priceLevel
                        capacity
                        imageUrls
                        checkinQRCode
                    }
                }
            `,
            variables() {
                return {
                    id: this.$route.params.id,
                }
            },
        },
    },
    data() {
        return {
            options: [{ value: 'live', text: 'Live' }],
            select: 'live',
            chart: null,
        }
    },
    computed: {
        labels() {
            if (!this.historicalData) {
                const times = this.venueAnalytics.visitors.timeDistribution.map(
                    (el) => new Date(Date.parse(el.timestamp)),
                )

                return [
                    this.fmt(times[24 - 18]),
                    '',
                    '',
                    this.fmt(times[24 - 12]),
                    '',
                    '',
                    this.fmt(times[24 - 6]),
                    '',
                    '',
                    this.fmt(times[24]),
                ]
            } else {
                return ['12am', '', '', '8am', '', '', '2pm', '', '', '9pm']
            }
        },
        chartData() {
            if (!this.venueAnalytics) return 0

            if (!this.historicalData) {
                const data = this.venueAnalytics.visitors.timeDistribution.slice(6, 26).map((el) => el.occurence)
                return this.chunkArray(data, 2)
            } else {
                const data = this.venueAnalytics.hourDistribution.map((el) => el.occurence)
                return this.chunkArray(data, 2.5)
            }
        },
        chartDataPercentage() {
            if (!this.venueAnalytics) return 0

            const data = this.chartData

            const max = Math.max(...data)
            return data.map((el) => (el / max) * 100)
        },
        currentVisitors() {
            if (!this.venueAnalytics) return 0

            const visitors = this.venueAnalytics.visitors
            return visitors.new + visitors.recurring + visitors.vip
        },
    },
    methods: {
        fmt(date, lang = 'en-EN', options = { hour: '2-digit' }) {
            return date.toLocaleTimeString(lang, options)
        },
        chunkArray(array, chunkSize) {
            let i,
                j,
                result = []

            for (i = 0, j = array.length; i < j; i += chunkSize) {
                if (i === j - 2) {
                    result.push(array[i + 1])
                } else {
                    const items = array.slice(i, i + chunkSize)
                    const sum = items[0] + items[1]
                    const total = sum > 0 ? sum / 2 : sum
                    result.push(Math.floor(total))
                }
            }

            return result
        },
    },
}
</script>

<style scoped lang="sass">
.bump-times
    width: 100%
    box-sizing: border-box

    .empty-state
        display: flex
        justify-content: center
        align-items: center
        height: 100px
        text-align: center
        user-select: none

    .card
        box-sizing: border-box
        min-height: 255px
        background: #ffffff !important
    header
        display: flex
        justify-content: space-between
        margin-bottom: $xl

        .d-select
            min-width: 80px

        .name
            color: $neutral-black
            font-weight: 600
            padding: $xs
            font-size: 14px
            line-height: 17px

    .chart-content
        margin: 0 auto
        width: 100%
        display: grid
        grid-row-gap: 12px
        grid-template-columns: repeat(10, 1fr)
        grid-template-areas: "data" "indicator" "label"

        .bar
            height: 116px
            width: 100%
            display: flex
            justify-content: center
            align-items: flex-end


            .data
                width: 20px
                border-radius: 7px
                background-color: rgba(3, 153, 218, 0.5)
                display: flex
                position: relative
                justify-content: center
                align-items: flex-end

                &:hover
                    .d-tooltip
                        opacity: 1
                        transform: translateY(0)

                &.zero
                    min-height: 4px
                    background-color: $gray-1

                &.current
                    background-color: #FF427E

                .current-visitors
                    margin: 2px
                    border-radius: 6px
                    width: 16px
                    background-color: #FF427E

        .indicator
            border-top: 1px solid $gray-1
            display: flex
            justify-content: center

            .inner-line
                width: 1px
                height: 8px
                background-color: $gray-1
        .label
            display: flex
            font-size: 12px
            justify-content: center
            color: #575962

        .chart-container
            width: 432px
</style>
